import { Controller } from "@hotwired/stimulus"

// Streaming + virtualized viewer for large plain-text files (logs, .txt, code, etc.)
//
// - Fetches the signed URL with a streaming fetch reader so the user sees content
//   appear as it downloads.
// - Splits the buffer into lines and only renders the visible window inside the
//   scroll container, so multi-MB files stay responsive.
//
// Targets:
//   scroller  - outer scrolling container
//   spacer    - inner element sized to (totalLines * lineHeight) for a real scrollbar
//   window    - absolutely positioned slice of <div> rows currently in view
//   status    - small text label in the toolbar (line count / size)
//   error / errorMessage - error overlay
//   wrapToggle - checkbox; turning on disables virtualization and renders a flat,
//                wrapped view of the loaded text.
export default class extends Controller {
  static targets = ["scroller", "spacer", "window", "status", "error", "errorMessage", "wrapToggle"]
  static values = {
    url: String,
    filename: String,
    // Cap to protect the browser. Files past this are still downloadable via the
    // toolbar download button.
    maxBytes: { type: Number, default: 100 * 1024 * 1024 }, // 100 MB
    overscan: { type: Number, default: 20 }
  }

  lines = [""]
  pendingTail = ""
  totalBytes = 0
  bytesRead = 0
  lineHeight = 20 // px, matches Tailwind leading-5 on text-xs
  digits = 1
  scrollHandler = null
  resizeHandler = null
  wrapMode = false
  abortController = null
  rafPending = false

  connect() {
    this.scrollHandler = () => this.scheduleRender()
    this.resizeHandler = () => this.scheduleRender()
    this.scrollerTarget.addEventListener("scroll", this.scrollHandler, { passive: true })
    window.addEventListener("resize", this.resizeHandler)
    this.startLoad()
  }

  disconnect() {
    if (this.abortController) this.abortController.abort()
    if (this.scrollHandler) this.scrollerTarget.removeEventListener("scroll", this.scrollHandler)
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler)
  }

  async startLoad() {
    if (!this.urlValue) {
      this.showError("No file URL")
      return
    }
    this.abortController = new AbortController()
    try {
      const response = await fetch(this.urlValue, { signal: this.abortController.signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      this.totalBytes = parseInt(response.headers.get("content-length") || "0", 10) || 0

      // A file past the cap still shows its head: the read loop below stops at
      // the cap and says the rest is in the download.
      const reader = response.body?.getReader()
      if (!reader) {
        // Without streaming, a known oversized body cannot be read safely.
        if (this.totalBytes > this.maxBytesValue) {
          this.showError(`File too large to preview (${this.formatBytes(this.totalBytes)}). Use Download to view locally.`)
          return
        }
        // Fallback: load whole body as text
        const text = await response.text()
        this.appendChunk(text, true)
        this.updateStatus()
        return
      }

      const decoder = new TextDecoder("utf-8", { fatal: false })
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          // Flush any decoder state and remaining tail line
          const tail = decoder.decode()
          if (tail) this.appendChunk(tail, false)
          this.flushPendingTail()
          this.updateStatus()
          this.scheduleRender()
          break
        }
        const remaining = this.maxBytesValue - this.bytesRead
        this.bytesRead += value.byteLength
        if (this.bytesRead > this.maxBytesValue) {
          this.appendChunk(decoder.decode(value.subarray(0, remaining)), false)
          this.appendChunk(`\n…truncated at ${this.formatBytes(this.maxBytesValue)} — download original to view full file.\n`, true)
          this.flushPendingTail()
          this.updateStatus({ truncated: true })
          this.abortController.abort()
          break
        }
        const chunk = decoder.decode(value, { stream: true })
        this.appendChunk(chunk, false)
        this.updateStatus({ loading: true })
        this.scheduleRender()
      }
    } catch (err) {
      if (err.name === "AbortError") return
      console.error("text-viewer load failed", err)
      this.showError(err.message || "Failed to load file")
    }
  }

  appendChunk(chunk, final) {
    if (!chunk) return
    const combined = this.pendingTail + chunk
    const parts = combined.split("\n")
    this.pendingTail = final ? "" : parts.pop()
    if (parts.length === 0) return

    // The first part continues the last in-progress line.
    if (this.lines.length === 0) this.lines.push("")
    this.lines[this.lines.length - 1] += parts[0]
    for (let i = 1; i < parts.length; i++) {
      this.lines.push(parts[i])
    }
    this.digits = String(this.lines.length).length
  }

  flushPendingTail() {
    if (this.pendingTail.length > 0) {
      this.lines[this.lines.length - 1] += this.pendingTail
      this.pendingTail = ""
      this.digits = String(this.lines.length).length
    }
  }

  scheduleRender() {
    if (this.rafPending) return
    this.rafPending = true
    requestAnimationFrame(() => {
      this.rafPending = false
      this.render()
    })
  }

  render() {
    if (this.wrapMode) {
      this.renderFlat()
      return
    }
    const totalLines = this.lines.length
    const totalHeight = totalLines * this.lineHeight
    this.spacerTarget.style.height = `${totalHeight}px`

    const viewportHeight = this.scrollerTarget.clientHeight
    const scrollTop = this.scrollerTarget.scrollTop
    const overscan = this.overscanValue

    const firstVisible = Math.max(0, Math.floor(scrollTop / this.lineHeight) - overscan)
    const visibleCount = Math.ceil(viewportHeight / this.lineHeight) + overscan * 2
    const lastVisible = Math.min(totalLines, firstVisible + visibleCount)

    const slice = this.lines.slice(firstVisible, lastVisible)
    const top = firstVisible * this.lineHeight
    this.windowTarget.style.transform = `translateY(${top}px)`

    const gutterPx = this.gutterWidth()
    const html = slice.map((line, idx) => {
      const lineNumber = firstVisible + idx + 1
      return `<div class="tv-row" style="height:${this.lineHeight}px;">` +
        `<div class="tv-gutter" style="width:${gutterPx}px;">${lineNumber}</div>` +
        `<div class="tv-line">${this.escape(line) || "\u200B"}</div>` +
        `</div>`
    }).join("")
    this.windowTarget.innerHTML = html
  }

  renderFlat() {
    // Wrap mode: render all lines stacked with wrapping enabled. Same row markup
    // as virtualized mode but with whitespace allowed to wrap.
    const totalLines = this.lines.length
    this.spacerTarget.style.height = "auto"
    this.windowTarget.style.transform = "translateY(0)"
    const gutterPx = this.gutterWidth()
    const html = this.lines.map((line, idx) => {
      const lineNumber = idx + 1
      return `<div class="tv-row tv-row-wrap">` +
        `<div class="tv-gutter" style="width:${gutterPx}px;">${lineNumber}</div>` +
        `<div class="tv-line tv-line-wrap">${this.escape(line) || "\u200B"}</div>` +
        `</div>`
    }).join("")
    this.windowTarget.innerHTML = html
  }

  toggleWrap(event) {
    this.wrapMode = !!event.currentTarget.checked
    // Reset window styles before re-render
    this.windowTarget.innerHTML = ""
    this.scheduleRender()
  }

  scrollToTop() {
    this.scrollerTarget.scrollTo({ top: 0 })
  }

  scrollToBottom() {
    this.scrollerTarget.scrollTo({ top: this.scrollerTarget.scrollHeight })
  }

  gutterWidth() {
    // ~8px per digit + padding; min 36px.
    return Math.max(36, this.digits * 9 + 18)
  }

  escape(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  updateStatus({ loading = false, truncated = false } = {}) {
    if (!this.hasStatusTarget) return
    const lineCount = this.lines.length.toLocaleString()
    const sizeLabel = this.totalBytes ? this.formatBytes(this.totalBytes) : this.formatBytes(this.bytesRead)
    let label = `${lineCount} lines · ${sizeLabel}`
    if (loading && this.totalBytes) {
      const pct = Math.min(100, Math.floor((this.bytesRead / this.totalBytes) * 100))
      label += ` · loading ${pct}%`
    } else if (loading) {
      label += " · loading…"
    }
    if (truncated) label += " · truncated"
    this.statusTarget.textContent = label
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.remove("hidden")
    this.errorTarget.classList.add("flex")
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message
    if (this.hasStatusTarget) this.statusTarget.textContent = "Failed to load"
  }

  formatBytes(bytes) {
    if (!bytes && bytes !== 0) return ""
    const units = ["B", "KB", "MB", "GB"]
    let value = bytes
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024
      unit++
    }
    return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
  }
}
